/**
 * ChatContext.js - Gerenciamento de Estado Global do Chat
 * 
 * OBJETIVO:
 * - Centralizar estado do chat (selectedChat, messages, chats, etc)
 * - Evitar prop drilling (passar props por vários níveis)
 * - Facilitar compartilhamento de estado entre ChatSidebar e ChatWindow
 * 
 * REFATORAÇÃO FASE 2.3:
 * - Extraído do ChatInterface.js (7418 linhas → ~300 linhas)
 * - Segue arquitetura tipo WhatsApp Web
 * 
 * Data: Novembro 2025
 * Versão: FASE 2.3
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { logger } from '../utils/logger';

const ChatContext = createContext();

export const ChatProvider = ({ children }) => {
  // ============================================
  // ESTADOS PRINCIPAIS
  // ============================================
  
  // Chat e mensagens
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chats, setChats] = useState([]);
  
  // Abas e filtros
  const [selectedTab, setSelectedTab] = useState('Atendimentos');
  const [atendSubTab, setAtendSubTab] = useState('Minhas Conversas');
  const [contactSearchTerm, setContactSearchTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Paginação
  const [chatPage, setChatPage] = useState(1);
  const [hasMoreChats, setHasMoreChats] = useState(true);
  const [isLoadingMoreChats, setIsLoadingMoreChats] = useState(false);
  
  // Loading states
  const [isTabLoading, setIsTabLoading] = useState(false);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Mensagens e status
  const [messageStatus, setMessageStatus] = useState('');
  const [messageError, setMessageError] = useState('');
  const [unreadCounts, setUnreadCounts] = useState({});

  // Total de não lidas exposto pra setar título da aba em outro lugar (dentro do Router).
  const unreadTotal = Object.values(unreadCounts).reduce((sum, n) => sum + (n || 0), 0);

  // Contadores
  const [contactsCounts, setContactsCounts] = useState(null);
  const [queueCountsReal, setQueueCountsReal] = useState(null);
  const [queueSortOrder, setQueueSortOrder] = useState('intelligent'); // 'intelligent' | 'oldest' | 'newest'
  
  // 🔥 SMART SORTING: Controles de visualização inteligente
  const [showSmartGrouping, setShowSmartGrouping] = useState(true); // Separar "Precisa Resposta" vs "Aguardando"
  
  // Usuários e grupos
  const [contacts, setContacts] = useState([]);
  const [internalUsers, setInternalUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  
  // UI States
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [shakeEnabled, setShakeEnabled] = useState(false);
  const [isNearTop, setIsNearTop] = useState(false);
  const [messagesLimit, setMessagesLimit] = useState(20);
  
  // Refs
  const lastLoadedChat = useRef(null);
  
  // ============================================
  // FUNÇÕES PRINCIPAIS
  // ============================================
  
  /**
   * Selecionar um chat e marcar como lido
   */
  const selectChat = useCallback((chat) => {
    if (!chat) {
      setSelectedChat(null);
      setMessages([]);
      return;
    }
    
    logger.log('💬 [ChatContext] Selecionando chat:', chat.id);
    
    // Atualizar chat selecionado
    setSelectedChat(chat);
    
    // Limpar mensagens antigas
    setMessages([]);
    
    // Resetar status de loading
    setIsLoadingMessages(false);
    
    // Marcar como último chat carregado
    lastLoadedChat.current = chat.id;
    
    // Limpar contador de não lidas para este chat
    setUnreadCounts(prev => {
      const updated = { ...prev };
      delete updated[chat.id];
      delete updated[chat.chat_id];
      delete updated[chat.contact_id];
      delete updated[chat.phoneNumber];
      return updated;
    });
  }, []);
  
  /**
   * Mudar de aba
   */
  const changeTab = useCallback((tab, subTab = null) => {
    logger.log('📑 [ChatContext] Mudando aba:', tab, subTab);
    
    setSelectedTab(tab);
    if (subTab !== null) {
      setAtendSubTab(subTab);
    }
    
    // Limpar chat selecionado ao mudar aba
    setSelectedChat(null);
    setMessages([]);
    
    // Resetar paginação
    setChatPage(1);
    setHasMoreChats(true);
    
    // Limpar busca
    setContactSearchTerm('');
  }, []);
  
  /**
   * Adicionar nova mensagem à lista
   */
  const addMessage = useCallback((message) => {
    setMessages(prev => [...prev, message]);
  }, []);
  
  /**
   * Atualizar chat na lista (ex: nova mensagem recebida)
   */
  const updateChat = useCallback((chatId, updates) => {
    setChats(prev => prev.map(chat => 
      (chat.id === chatId || chat.chat_id === chatId) 
        ? { ...chat, ...updates }
        : chat
    ));
  }, []);
  
  /**
   * Mover chat para o topo da lista
   */
  const moveToTop = useCallback((chatId) => {
    setChats(prev => {
      const chatIndex = prev.findIndex(c => 
        c.id === chatId || c.chat_id === chatId
      );
      
      if (chatIndex === -1) return prev;
      
      const chat = prev[chatIndex];
      const newChats = [...prev];
      newChats.splice(chatIndex, 1);
      return [chat, ...newChats];
    });
  }, []);
  
  /**
   * Resetar estado do chat
   */
  const resetChatState = useCallback(() => {
    setChats([]);
    setSelectedChat(null);
    setMessages([]);
    setChatPage(1);
    setHasMoreChats(true);
    setIsLoadingMoreChats(false);
    setContactSearchTerm('');
  }, []);
  
  // ============================================
  // CONTEXT VALUE
  // ============================================
  
  const value = {
    // Estados
    selectedChat,
    messages,
    chats,
    selectedTab,
    atendSubTab,
    contactSearchTerm,
    searchTerm,
    chatPage,
    hasMoreChats,
    isLoadingMoreChats,
    isTabLoading,
    isChatLoading,
    isLoadingMessages,
    loading,
    messageStatus,
    messageError,
    unreadCounts,
    unreadTotal,
    contactsCounts,
    queueCountsReal,
    queueSortOrder,
    showSmartGrouping,
    contacts,
    internalUsers,
    groups,
    usersMap,
    soundEnabled,
    shakeEnabled,
    isNearTop,
    messagesLimit,
    lastLoadedChat,
    
    // Setters
    setSelectedChat,
    setMessages,
    setChats,
    setSelectedTab,
    setAtendSubTab,
    setContactSearchTerm,
    setSearchTerm,
    setChatPage,
    setHasMoreChats,
    setIsLoadingMoreChats,
    setIsTabLoading,
    setIsChatLoading,
    setIsLoadingMessages,
    setLoading,
    setMessageStatus,
    setMessageError,
    setUnreadCounts,
    setContactsCounts,
    setQueueCountsReal,
    setQueueSortOrder,
    setShowSmartGrouping,
    setContacts,
    setInternalUsers,
    setGroups,
    setUsersMap,
    setSoundEnabled,
    setShakeEnabled,
    setIsNearTop,
    setMessagesLimit,
    
    // Funções
    selectChat,
    changeTab,
    addMessage,
    updateChat,
    moveToTop,
    resetChatState
  };
  
  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

/**
 * Hook para usar o ChatContext em qualquer componente
 */
export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext deve ser usado dentro de ChatProvider');
  }
  return context;
};

export default ChatContext;

